/**
 * The pure decisions behind the PAUSED-subscription controls on the User
 * Profile page (#4189), extracted so they can be unit-tested without a page
 * harness — the same shape as `pricing-failure-policy.ts` next door.
 *
 * These are worth pinning because a paused subscription is the one state a user
 * cannot recover from on their own if the UI gets it wrong. A pause that has
 * taken effect grants no access, so the paid role drops to Unknown; every
 * "are they a paying customer?" condition on the page answers NO. If the Resume
 * button is gated on those conditions it never renders, and the subscriber is
 * stranded with no in-app way back.
 */

/** Whether to show the Resume button. */
export function shouldShowResumeButton(state: {
  /** the page's role bucket — 'paid' only while access is live */
  roleClass: string;
  /** false once a CANCEL is already scheduled — a scheduled pause leaves it true */
  canCancelSubscription: boolean;
  /** a temporary (granted) licence is not a cancellable subscription */
  roleIsTemporary: boolean;
  /** a PAUSE is scheduled but has not taken effect yet (so the subscriber is
   *  still paying and still 'paid') AND the server says resuming would work.
   *  Both halves are required — the arm below says why. */
  hasResumablePendingPause: boolean;
  /** the pause has taken effect: non-granting, so roleClass is NOT 'paid' */
  isPaddlePaused: boolean;
  /** an in-app resume is actually reachable on this platform */
  canResumeHere: boolean;
}): boolean {
  // the ordinary case: a paying subscriber who has already scheduled a cancel.
  const isCanceledPaid = state.roleClass === 'paid'
    && !state.canCancelSubscription
    && !state.roleIsTemporary;
  // A subscriber with a pause SCHEDULED is still paying and still cancellable,
  // so canCancelSubscription stays true for them and the arm above can never
  // match. Resuming un-schedules the pause before it applies, and
  // PADDLE_RULES.canCancel in getSubscriptionInfo.ts states the intended result
  // outright: they get both Cancel and Resume. Without this arm they got neither
  // the Resume button nor any mention of the pause (deferred finding
  // df-b4b8eb01434d).
  //
  // Unlike the two arms around it, this one does NOT decide resumability from
  // local state — it takes the server's answer, which is what
  // `hasResumablePendingPause` carries. One state makes the difference:
  // PADDLE_RULES.canResume refuses a DUNNING (past_due) subscription unless the
  // pending change is a cancel, because resuming cannot make a failed charge
  // succeed. Such a member still reads as 'paid' locally, so deciding from local
  // state alone would show them a button whose handler, finding the server flag
  // false, sends a member who already pays us to the Subscribe page to buy a
  // SECOND membership — the dead end #4247 forbids. The other two arms cannot
  // reach it: the server's dunning branch answers true for exactly the pending
  // cancel `isCanceledPaid` keys on, and an already-paused subscription is not
  // Suspended.
  const isPausePendingPaid = state.roleClass === 'paid'
    && state.hasResumablePendingPause
    && !state.roleIsTemporary;
  // isPaddlePaused is ORed in because a taken-effect pause drops the paid role,
  // so neither arm above can match for an already-paused subscriber. This is
  // the whole reason the paused user has a way back.
  return (isCanceledPaid || isPausePendingPaid || state.isPaddlePaused) && state.canResumeHere;
}

// There is deliberately no shouldShowPauseButton: the app never offers pause
// (#4189). Everything else in this file is about DISPLAYING a paused
// subscription and getting out of one, which stays reachable because Paddle
// Retain's cancellation flow can pause a subscription without our involvement.

/**
 * Whether to suppress the free-user "Upgrade" block. A paused subscriber looks
 * exactly like a free user to the role check (no access), so without this they
 * would be invited to BUY a second subscription instead of resuming the one
 * they already have — a real double charge, and it orphans the paused sub,
 * which stays alive and resumable at Paddle.
 */
export function shouldSuppressUpgradeBlock(state: {
  /** The page's role bucket — an `AuthRoleDesc.id`, so it can also be `vip`,
   *  `mod`, `dev` and so on. Taken as a plain `string` rather than the
   *  `AuthRoleId` union deliberately: this policy only asks whether the bucket
   *  IS `'unknown'`, so it does not need to know the full set, and keeping it
   *  loose means a new role never has to be added here. Only `'unknown'` is
   *  significant (a paused subscriber's access has stopped, so they fall into
   *  the free bucket). A holder of any OTHER role has access for an unrelated
   *  reason and must keep seeing the normal page. */
  roleClass: string;
  isPaddlePaused: boolean;
}): boolean {
  return state.roleClass === 'unknown' && state.isPaddlePaused;
}
